import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { getProductAvailability } from '../../../../api/products';
import { getRequestedLanguages } from '../../../../locale/languages';
import { ACCOUNT_TYPES } from '../../../../utils/enum/AccountTypes';
import { AVAILABLE_STATUS } from '../../../../utils/enum/AvailableStatus';
import { ICONS } from '../../../../utils/enum/Icons';
import { convertNumberToCurrency } from '../../../../utils/functions';
import { CartItem } from '../../../../utils/models/order';
import { ProductProperty, ProductVariant } from '../../../../utils/models/product';
import AvailableStatusComponent from '../../../AvailableStatusComponent';
import ButtonComponent from '../../../ButtonComponent';
import EditIcon from '../../../IconsSvg/EditIcon';
import LoadingSpinnerComponent from '../../../LoadingSpinnerComponent';
import QuantitySelectorComponent from '../../../QuantitySelectorComponent';
import ToolTipComponent from '../../../TooltipComponent';
import AssociateIdModalComponent from '../../../modals/AssociateIdModalComponent';

interface Props {
    customerCode: string;
    variant: ProductVariant;
    properties: string[];
    onAddToCart: (cartItem: CartItem) => void;
    onUpdateExternalCode: (id: number, externalCode: string) => void;
}

const VariantRow: FC<Props> = ({ variant, properties, onAddToCart, onUpdateExternalCode, customerCode }) => {
    const [currentVariant, setCurrentVariant] = useState<ProductVariant>(variant);
    const language = getRequestedLanguages(window.berardi_accountUserData.language);
    const [showAllProperties, setShowAllProperties] = useState(false);
    const [selectedQuantity, setSelectedQuantity] = useState(0);
    const [statusAvailability, setStatusAvailability] = useState<AVAILABLE_STATUS | null>(null);
    const [loadingAvailability, setLoadingAvailability] = useState(false);
    const [showModalAssociate, setShowModalAssociate] = useState(false);
    const [hasFetchedQuantity, setHasFetchedQuantity] = useState(false);

    const disableAddToCart = useMemo(() => {
        if (
            window.berardi_accountUserData.accountType == ACCOUNT_TYPES.Agent ||
            !window.berardi_accountUserData.accountType ||
            window.berardi_accountUserData.accountType == ACCOUNT_TYPES.CustomerService ||
            window.berardi_accountUserData.accountType == ACCOUNT_TYPES.Guest
        ) {
            return true;
        }
        if (selectedQuantity == 0 || loadingAvailability) {
            return true;
        }
        return false;
    }, [statusAvailability, selectedQuantity, loadingAvailability]);

    const handleOpenDetails = useCallback(() => {
        setShowAllProperties(!showAllProperties);
    }, [showAllProperties]);

    const onChangeQuantity = useCallback(
        (newQuantity: number) => {
            setSelectedQuantity(newQuantity);
            if (currentVariant.quantity.max != null) {
                if (currentVariant.quantity.max == 0) {
                    setStatusAvailability(AVAILABLE_STATUS.NotAvailable);
                    return;
                }
                if (newQuantity > currentVariant.quantity.max || newQuantity * currentVariant.price > 500) {
                    setStatusAvailability(AVAILABLE_STATUS.InsufficientQty);
                    return;
                }
                setStatusAvailability(AVAILABLE_STATUS.Available);
            }
        },
        [currentVariant.quantity.max],
    );

    const handleFetchAvailability = useCallback(async () => {
        setLoadingAvailability(true);
        try {
            const newQuantityFetched = await getProductAvailability(currentVariant.sku, customerCode ?? null).then(
                (result) => {
                    return result.data;
                },
            );
            setHasFetchedQuantity(true);
            const quantityObject = newQuantityFetched.data.quantity ?? null;
            if (quantityObject) {
                setCurrentVariant({
                    ...currentVariant,
                    quantity: quantityObject,
                });
                const maxValue = quantityObject.max as number;
                if (maxValue > 0) {
                    if (selectedQuantity < maxValue) {
                        setStatusAvailability(AVAILABLE_STATUS.Available);
                    } else {
                        setStatusAvailability(AVAILABLE_STATUS.InsufficientQty);
                    }
                } else {
                    setStatusAvailability(AVAILABLE_STATUS.NotAvailable);
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoadingAvailability(false);
    }, [currentVariant, selectedQuantity]);

    const handleAddToCartVariant = useCallback(() => {
        if (!disableAddToCart) {
            const cartItem: CartItem = {
                code: currentVariant.external_code?.toString() ?? '',
                details: currentVariant.properties.map((property) => {
                    return {
                        label: property.code,
                        value: property.value,
                    };
                }),
                id: currentVariant.id,
                isQuote:
                    window.berardi_accountUserData.accountType == ACCOUNT_TYPES.Prospect ||
                    statusAvailability == AVAILABLE_STATUS.NotAvailable
                        ? true
                        : false,
                featuredImage: window.berardi_productData.defaultImage,
                unitPrice: currentVariant.price,
                quantity: selectedQuantity,
                quantityDiscount: 0,
                minQuantityDiscount: 0,
                sku: currentVariant.sku,
                step: currentVariant.quantity.step,
                min: currentVariant.quantity.min,
                max: currentVariant.quantity.max,
                title: `${currentVariant.name}`,
                url: window.berardi_productData.url,
                availableStatus: statusAvailability || AVAILABLE_STATUS.Available,
            };
            onAddToCart(cartItem);
        }
    }, [currentVariant, selectedQuantity, disableAddToCart, statusAvailability]);

    const limitedProperties = useMemo(() => {
        if (currentVariant.properties.length > 6) {
            return currentVariant.properties.slice(0, 6);
        }
        return currentVariant.properties;
    }, [currentVariant]);

    const artCodeCell = useMemo(() => {
        if (customerCode) {
            return (
                <>
                    <label>
                        <b>{language.product.table.art_code}</b> / {language.product.table.art_code_customer}
                    </label>
                    <span>
                        <b>{currentVariant.sku}</b> /<br />{' '}
                        {currentVariant.external_code ? (
                            <span className="br-table__rowCell--externalId">
                                {currentVariant.external_code}{' '}
                                <EditIcon
                                    onClick={() => {
                                        setShowModalAssociate(true);
                                    }}
                                />
                            </span>
                        ) : (
                            <ButtonComponent
                                text={language.product.table.associate}
                                onClick={() => {
                                    setShowModalAssociate(true);
                                }}
                                style="link-light"
                                customStyle={{
                                    color: '#005092',
                                    padding: 0,
                                    fontWeight: 400,
                                }}
                            />
                        )}
                    </span>
                </>
            );
        }
        return (
            <>
                <label>
                    <b>{language.product.table.art_code}</b>
                </label>
                <span>
                    <b>{currentVariant.sku}</b>
                </span>
            </>
        );
    }, [variant, currentVariant, customerCode]);

    const availableLabel = useMemo(() => {
        if (loadingAvailability) {
            return (
                <div className="loadAvailability">
                    <LoadingSpinnerComponent />
                </div>
            );
        }
        if (statusAvailability != null) {
            return <AvailableStatusComponent status={statusAvailability} />;
        }
        return null;
    }, [loadingAvailability, statusAvailability]);

    const showAddToCartButton = useMemo(() => {
        if (
            window.berardi_accountUserData.accountType &&
            window.berardi_accountUserData.accountType != ACCOUNT_TYPES.Agent &&
            window.berardi_accountUserData.accountType != ACCOUNT_TYPES.CustomerService &&
            window.berardi_accountUserData.accountType != ACCOUNT_TYPES.Guest
        ) {
            return true;
        }
        return false;
    }, []);

    useEffect(() => {
        if (selectedQuantity > 0) {
            if (
                !statusAvailability &&
                !loadingAvailability &&
                !hasFetchedQuantity &&
                customerCode &&
                !disableAddToCart &&
                window.berardi_accountUserData.accountType != ACCOUNT_TYPES.Prospect
            ) {
                handleFetchAvailability();
            }
        }
    }, [statusAvailability, selectedQuantity, loadingAvailability]);

    useEffect(() => {
        setCurrentVariant(variant);
    }, [variant]);

    return (
        <tr className="br-table__row" key={currentVariant.sku}>
            <td className="br-table__rowCell -flex2 -full-width -artCode">{artCodeCell}</td>
            {showAllProperties
                ? currentVariant.properties.map((property: ProductProperty) => {
                      return (
                          <td className="br-table__rowCell">
                              <label>{property.code}</label>
                              {property.value}
                          </td>
                      );
                  })
                : limitedProperties.map((property: ProductProperty) => {
                      return (
                          <td className="br-table__rowCell">
                              <label>{property.code}</label>
                              {property.value}
                          </td>
                      );
                  })}
            {/* <th className="br-table__rowCell">
                                        <label>{language.product.table.weight}</label>
                                        {currentVariant.weight}
                                    </th>
                                    <th className="br-table__rowCell">
                                        <label>{language.product.table.pieces_per_package}</label>
                                        {currentVariant.box}
                                    </th> */}

            <td className={`br-table__rowCell -hideMobile`}>
                <ToolTipComponent icon="info">
                    <div className="moreProperties">
                        {currentVariant.properties.map((property) => {
                            const isInLimited = limitedProperties.find(
                                (limitedProperty) => limitedProperty.code == property.code,
                            );
                            if (isInLimited) {
                                return null;
                            }
                            return (
                                <p className="moreProperties__row">
                                    <span>{property.code}</span> {property.value}
                                </p>
                            );
                        })}
                        <p className="moreProperties__row">
                            <span>{language.product.table.weight}</span>
                            {currentVariant.weight}
                        </p>
                        <p className="moreProperties__row">
                            <span>{language.product.table.pieces_per_package}</span>
                            {currentVariant.box}
                        </p>
                    </div>
                </ToolTipComponent>
            </td>
            {window.berardi_accountUserData.accountType != ACCOUNT_TYPES.Prospect && (
                <td className={`br-table__rowCell ${!showAllProperties && '-hideMobile'}`}>
                    <label>{language.product.table.unit_price}</label>
                    {customerCode ? (
                        convertNumberToCurrency(currentVariant.price, 'EUR', 5)
                    ) : (
                        <ToolTipComponent icon="warning">
                            <p>{language.product.table.login_to_see_price}</p>
                        </ToolTipComponent>
                    )}
                </td>
            )}

            <th className="br-table__rowCell -numberPieces -hideMobile">
                <label>{language.product.table.number_of_pieces}</label>
                <QuantitySelectorComponent
                    quantity={selectedQuantity}
                    onChange={onChangeQuantity}
                    minimum={currentVariant.quantity.min ?? 1}
                    multiple={currentVariant.quantity.step ?? 1}
                    maximum={currentVariant.quantity.max}
                    disabledButtons={loadingAvailability}
                />
                {availableLabel}
                {/* {currentVariant.order_multiple} */}
            </th>
            {customerCode && window.berardi_accountUserData.accountType != ACCOUNT_TYPES.Prospect ? (
                <td className={`br-table__rowCell -price -hideMobile`}>
                    <label>{language.product.table.total_price}</label>
                    {convertNumberToCurrency(currentVariant.price * selectedQuantity)}
                </td>
            ) : null}
            <td className="br-table__rowCell -flex2 -hideDesktop -full-width -detailsButton">
                <label onClick={handleOpenDetails}>
                    {showAllProperties ? '-' : '+'} {language.product.table.details}
                </label>
            </td>
            {showAddToCartButton && (
                <td className="br-table__rowCell -hideMobile">
                    <ButtonComponent
                        style="circle-icon"
                        text=""
                        icon_left={ICONS.cart}
                        onClick={handleAddToCartVariant}
                        disabled={disableAddToCart}
                    />
                </td>
            )}

            <td className="br-table__rowCell -recapMobileRow -hideDesktop">
                <div className="recapCell">
                    <label>{language.product.table.pieces}</label>
                    <QuantitySelectorComponent
                        quantity={selectedQuantity}
                        onChange={setSelectedQuantity}
                        minimum={currentVariant.quantity.min ?? 1}
                        multiple={currentVariant.quantity.step ?? 1}
                        maximum={currentVariant.quantity.max}
                        disabledButtons={loadingAvailability}
                    />
                </div>
                {customerCode ? (
                    <div className="recapCell">{convertNumberToCurrency(currentVariant.price * selectedQuantity)}</div>
                ) : null}
                {showAddToCartButton && (
                    <div className="recapCell">
                        <ButtonComponent
                            style="circle-icon"
                            text=""
                            icon_left={ICONS.cart}
                            onClick={handleAddToCartVariant}
                            disabled={disableAddToCart}
                        />
                    </div>
                )}
            </td>
            {availableLabel ? (
                <td className="br-table__rowCell -availabilityMobileRow -hideDesktop">{availableLabel}</td>
            ) : null}
            <AssociateIdModalComponent
                showModal={showModalAssociate}
                variant={variant}
                customerCode={customerCode}
                properties={properties}
                onCancel={() => setShowModalAssociate(false)}
                onSave={(code) => {
                    setShowModalAssociate(false);
                    onUpdateExternalCode(variant.id, code);
                }}
            />
        </tr>
    );
};

export default VariantRow;
